import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plug, Plus, Play, Trash2, RefreshCw, CheckCircle2, AlertCircle,
  Cloud, Loader2, X, Link2, BookOpen, ChevronDown, ChevronRight, ExternalLink
} from 'lucide-react';
import { connectorsAPI, accountsAPI, tenantsAPI, connectorSourceLabels } from '../services/amidala';
import { toast } from 'sonner';

const CONNECTOR_META = {
  security_hub: {
    label: 'AWS Security Hub',
    icon: '☁️',
    color: 'border-orange-300 bg-orange-50',
    guide: {
      docs: 'https://docs.aws.amazon.com/securityhub/latest/userguide/securityhub-cross-account.html',
      steps: [
        'In the AWS account you want to monitor, open IAM and create a new Role.',
        'Choose "AWS account" → "Another AWS account" as trusted entity. Enter the Amidala AWS Account ID (provided by Beskar) and your chosen External ID.',
        'Attach the AWS managed policy "SecurityAudit" (or the SecurityHub read-only policy) so Amidala can call securityhub:GetFindings & DescribeHub.',
        'Copy the Role ARN (e.g., arn:aws:iam::123456789012:role/AmidalaSecurityHub) and the External ID into the Add Connector form on the right.',
        'Pick the region where Security Hub is enabled (e.g., us-east-1). Save and click "Test" to verify the trust policy works.',
      ],
    },
  },
  guardduty: {
    label: 'AWS GuardDuty',
    icon: '🛡️',
    color: 'border-amber-300 bg-amber-50',
    guide: {
      docs: 'https://docs.aws.amazon.com/guardduty/latest/ug/guardduty_findings.html',
      steps: [
        'Use the same IAM Role as Security Hub OR create a dedicated cross-account role.',
        'Attach AWS managed policy "AmazonGuardDutyReadOnlyAccess" so Amidala can list detectors and call get_findings.',
        'Ensure GuardDuty is enabled in at least one region of the target account.',
        'Copy the Role ARN and region into the Add Connector form, then click "Test".',
      ],
    },
  },
  defender_for_cloud: {
    label: 'Microsoft Defender for Cloud',
    icon: '🔷',
    color: 'border-blue-300 bg-blue-50',
    guide: {
      docs: 'https://learn.microsoft.com/en-us/azure/defender-for-cloud/alerts-overview',
      steps: [
        'In the Azure portal, register a new App in Microsoft Entra ID (Azure AD).',
        'Note its Application (client) ID, Directory (tenant) ID, and create a Client Secret.',
        'In the target subscription, grant the App the "Security Reader" RBAC role under Subscription → Access control (IAM) → Add role assignment.',
        'In the Add Connector form, paste Subscription ID, Tenant ID (Azure tenant — NOT Amidala tenant), Client ID, and Client Secret.',
        'Click "Test" to verify the App registration can list Defender alerts.',
      ],
    },
  },
  gcp_scc: {
    label: 'Google Cloud Security Command Center',
    icon: '🌐',
    color: 'border-emerald-300 bg-emerald-50',
    guide: {
      docs: 'https://cloud.google.com/security-command-center/docs/how-to-grant-access',
      steps: [
        'Enable Security Command Center on the target GCP Organization (Standard or Premium tier).',
        'Create a Service Account in any project under that organization.',
        'Grant the Service Account these IAM roles at the Organization level: "Security Center Findings Viewer" and "Security Center Settings Viewer".',
        'Generate a JSON key for the Service Account and download it. Open the JSON file and paste its entire contents into the "service_account_json" field on the Add Connector form.',
        'Provide the Organization ID (numeric — visible in Cloud Console → IAM & Admin → Settings). Click "Test".',
      ],
    },
  },
};

const fieldHelp = {
  role_arn: 'IAM Role ARN with permissions to read findings (uses STS AssumeRole).',
  external_id: 'Optional STS External ID for cross-account trust policy.',
  region: 'AWS region (e.g., us-east-1).',
  subscription_id: 'Azure subscription ID containing Defender for Cloud.',
  tenant_id: 'Azure tenant ID (directory ID).',
  client_id: 'App registration client ID.',
  client_secret: 'App registration client secret.',
  organization_id: 'GCP organization ID where Security Command Center is enabled.',
  service_account_json: 'Paste the full service-account JSON key with securitycenter.findings.list permission.',
};

const AmidalaConnectors = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/amidala') ? '/amidala' : '/grc/amidala';

  const [tenant, setTenant] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [supported, setSupported] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [showGuide, setShowGuide] = useState(true);
  const [openGuide, setOpenGuide] = useState(null);
  const [form, setForm] = useState({
    name: '',
    connector_type: 'security_hub',
    account_id: '',
    reuse_account_credentials: true,
    sync_enabled: true,
    sync_interval_minutes: 15,
    credentials: {},
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const tenantsRes = await tenantsAPI.list();
      const t = tenantsRes.data[0];
      if (!t) {
        setLoading(false);
        return;
      }
      setTenant(t);
      const [sup, conns, accts] = await Promise.all([
        connectorsAPI.listSupported(),
        connectorsAPI.list(t.id),
        accountsAPI.list(t.id),
      ]);
      setSupported(sup.data || []);
      setConnectors(conns.data || []);
      setAccounts(accts.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load connectors');
    } finally {
      setLoading(false);
    }
  };

  const currentSpec = supported.find(s => s.type === form.connector_type);

  const resetForm = () => {
    setForm({
      name: '',
      connector_type: 'security_hub',
      account_id: '',
      reuse_account_credentials: true,
      sync_enabled: true,
      sync_interval_minutes: 15,
      credentials: {},
    });
  };

  const handleCreate = async () => {
    if (!tenant) return;
    if (!form.name.trim()) {
      toast.error('Connector name is required');
      return;
    }
    try {
      await connectorsAPI.create(tenant.id, form);
      toast.success('Connector created');
      setShowAdd(false);
      resetForm();
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create connector');
    }
  };

  const handleTest = async (c) => {
    setTesting(c.id);
    try {
      const r = await connectorsAPI.test(c.id);
      if (r.data.success) toast.success(r.data.message || 'Connection OK');
      else toast.error(r.data.message || 'Connection failed');
    } catch {
      toast.error('Connection test failed');
    } finally {
      setTesting(null);
    }
  };

  const handleSync = async (c) => {
    setSyncing(c.id);
    try {
      const r = await connectorsAPI.sync(c.id);
      const d = r.data;
      if (d.status === 'success') {
        toast.success(`Sync complete: +${d.findings_imported} new, ${d.findings_updated} updated`);
      } else {
        toast.error(d.message || 'Sync failed');
      }
      await loadAll();
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete connector "${c.name}"? Imported findings will remain.`)) return;
    try {
      await connectorsAPI.delete(c.id);
      toast.success('Connector deleted');
      await loadAll();
    } catch {
      toast.error('Delete failed');
    }
  };

  const updateCredential = (k, v) => {
    setForm(prev => ({ ...prev, credentials: { ...prev.credentials, [k]: v } }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="amidala-connectors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Plug className="w-8 h-8 text-indigo-600" />
            External Security Connectors
          </h1>
          <p className="text-gray-600 mt-1">
            Continuously import findings from AWS Security Hub, GuardDuty, Defender for Cloud, and Google SCC.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          data-testid="add-connector-btn"
        >
          <Plus className="w-4 h-4" /> Add Connector
        </button>
      </div>

      {/* Setup Guide Panel */}
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden" data-testid="connector-setup-guide">
        <button
          type="button"
          onClick={() => setShowGuide((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50"
          data-testid="connector-guide-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="text-left">
              <h3 className="text-base font-semibold text-gray-900">How to set up a connector</h3>
              <p className="text-sm text-gray-500">Step-by-step IAM/RBAC setup for each supported cloud security tool.</p>
            </div>
          </div>
          {showGuide ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
        </button>

        {showGuide && (
          <div className="border-t border-gray-200 divide-y divide-gray-100">
            {Object.entries(CONNECTOR_META).map(([key, meta]) => {
              const expanded = openGuide === key;
              return (
                <div key={key} data-testid={`connector-guide-${key}`}>
                  <button
                    type="button"
                    onClick={() => setOpenGuide(expanded ? null : key)}
                    className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50"
                    data-testid={`connector-guide-toggle-${key}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{meta.icon}</span>
                      <span className="text-sm font-medium text-gray-900">{meta.label}</span>
                    </div>
                    {expanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                  </button>
                  {expanded && meta.guide && (
                    <div className="px-5 py-4 bg-gray-50">
                      <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                        {meta.guide.steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                      {meta.guide.docs && (
                        <a
                          href={meta.guide.docs}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                          data-testid={`connector-guide-docs-${key}`}
                        >
                          Official documentation <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => {
                            setForm((prev) => ({ ...prev, connector_type: key, credentials: {} }));
                            setShowAdd(true);
                          }}
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                          data-testid={`connector-guide-start-${key}`}
                        >
                          → Start adding {meta.label}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Connector grid */}
      {connectors.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-10 text-center" data-testid="connectors-empty-state">
          <Plug className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No connectors yet</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Add a connector to continuously stream alerts and findings from your existing cloud-native security tools into Amidala.
          </p>
          <button
            onClick={() => setShowAdd(true)}
            className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Configure your first connector
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="connectors-list">
          {connectors.map((c) => {
            const meta = CONNECTOR_META[c.connector_type] || { label: c.connector_type, icon: '🔌', color: '' };
            const linkedAccount = accounts.find(a => a.id === c.account_id);
            return (
              <div key={c.id} className={`bg-white rounded-xl shadow border-l-4 ${meta.color} p-5`} data-testid={`connector-card-${c.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{meta.icon}</span>
                    <div>
                      <h3 className="font-bold text-gray-900">{c.name}</h3>
                      <p className="text-xs text-gray-500">{meta.label}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    c.status === 'active' ? 'bg-emerald-100 text-emerald-700'
                    : c.status === 'error' ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-700'
                  }`}>{c.status}</span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div>
                    <p className="text-gray-500 text-xs">Linked account</p>
                    <p className="font-medium text-gray-900 truncate">{linkedAccount?.name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Last sync</p>
                    <p className="font-medium text-gray-900">
                      {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : 'Never'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Imported (lifetime)</p>
                    <p className="font-medium text-gray-900">{c.total_imported ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Interval</p>
                    <p className="font-medium text-gray-900">{c.sync_interval_minutes}m</p>
                  </div>
                </div>

                {c.last_sync_message && (
                  <p className={`text-xs px-3 py-2 rounded mb-3 ${
                    c.last_sync_status === 'success'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-red-50 text-red-700'
                  }`}>{c.last_sync_message}</p>
                )}

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleTest(c)}
                    disabled={testing === c.id}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                    data-testid={`connector-test-${c.id}`}
                  >
                    {testing === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                    Test
                  </button>
                  <button
                    onClick={() => handleSync(c)}
                    disabled={syncing === c.id}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    data-testid={`connector-sync-${c.id}`}
                  >
                    {syncing === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    Sync now
                  </button>
                  <button
                    onClick={() => handleDelete(c)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50"
                    data-testid={`connector-delete-${c.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Connector modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-connector-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Add Connector</h2>
              <button onClick={() => { setShowAdd(false); resetForm(); }} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Connector Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {supported.map(s => (
                    <button
                      type="button"
                      key={s.type}
                      onClick={() => setForm(prev => ({ ...prev, connector_type: s.type, credentials: {} }))}
                      className={`p-3 rounded-lg border-2 text-left ${
                        form.connector_type === s.type
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`connector-type-${s.type}`}
                    >
                      <p className="text-2xl">{CONNECTOR_META[s.type]?.icon || '🔌'}</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">{s.name}</p>
                      <p className="text-xs text-gray-500">{s.provider.toUpperCase()}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Prod Security Hub - us-east-1"
                  data-testid="connector-name-input"
                />
              </div>

              {currentSpec?.reuse_account_credentials && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Linked Cloud Account (optional)</label>
                  <select
                    value={form.account_id}
                    onChange={(e) => setForm(prev => ({ ...prev, account_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="connector-account-select"
                  >
                    <option value="">— None —</option>
                    {accounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({a.provider})</option>
                    ))}
                  </select>
                  {form.account_id && (
                    <label className="inline-flex items-center gap-2 mt-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.reuse_account_credentials}
                        onChange={(e) => setForm(prev => ({ ...prev, reuse_account_credentials: e.target.checked }))}
                        data-testid="connector-reuse-creds"
                      />
                      Reuse credentials from this cloud account
                    </label>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3">
                {(currentSpec?.required_fields || []).map(field => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field} <span className="text-red-500">*</span>
                    </label>
                    {field === 'service_account_json' ? (
                      <textarea
                        value={form.credentials[field] || ''}
                        onChange={(e) => updateCredential(field, e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs"
                        placeholder='{"type":"service_account",...}'
                        data-testid={`cred-${field}`}
                      />
                    ) : (
                      <input
                        type={field.includes('secret') || field.includes('key') ? 'password' : 'text'}
                        value={form.credentials[field] || ''}
                        onChange={(e) => updateCredential(field, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        data-testid={`cred-${field}`}
                      />
                    )}
                    <p className="text-xs text-gray-500 mt-1">{fieldHelp[field] || ''}</p>
                  </div>
                ))}
                {(currentSpec?.optional_fields || []).map(field => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field} <span className="text-gray-400 text-xs">(optional)</span>
                    </label>
                    <input
                      value={form.credentials[field] || ''}
                      onChange={(e) => updateCredential(field, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      data-testid={`cred-${field}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">{fieldHelp[field] || ''}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sync interval (minutes)</label>
                  <input
                    type="number"
                    min={5}
                    max={1440}
                    value={form.sync_interval_minutes}
                    onChange={(e) => setForm(prev => ({ ...prev, sync_interval_minutes: parseInt(e.target.value, 10) || 15 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="connector-interval"
                  />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm pb-2">
                    <input
                      type="checkbox"
                      checked={form.sync_enabled}
                      onChange={(e) => setForm(prev => ({ ...prev, sync_enabled: e.target.checked }))}
                      data-testid="connector-sync-enabled"
                    />
                    Enable background sync
                  </label>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowAdd(false); resetForm(); }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="connector-create-submit"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmidalaConnectors;
